<template>
  <div v-rect-loading="loading" class="source-code-editor-page">
    <div class="source-code-editor-header">
      <div class="source-code-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="form"
        class="source-code-management-info"
        label-width="220px"
        :model="sourceCodeData"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty, selectLabelI18n } from '@/i18n'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'

export default {
  components: {
    WrappedFrom
  },
  props: {

  },
  data () {
    return {
      i18nPrefix: 'admin.company.sourceChannelManagement',
      loading: false,
      sourceCodeData: {},
      sourceChannelList: []
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}${this.isEditMode ? '.label.sourceCodeEdit' : '.label.sourceCodeAdd'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    sourceCodeId () {
      return this.$route.params.codeId
    },
    companyId () {
      return this.$route.query.company
    },
    formLines () {
      const lines = {
        source: {
          disabled: this.isEditMode,
          label: this.$t(`${this.i18nPrefix}.label.source`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.source`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.required`), trigger: 'blur' }]
        },
        code: {
          disabled: this.isEditMode,
          label: this.$t(`${this.i18nPrefix}.label.code`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.code`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.required`), trigger: 'blur' }]
        },
        channel_id: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.channel`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.channel`),
          props: {
            filterable: true
          },
          option: {
            data: this.sourceChannelList,
            valueKey: 'id',
            labelKey: 'channelName'
          },
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.required`), trigger: 'blur' }]
        }
      }
      return lines
    },
    form () {
      const form = this.$refs.form
      return form
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('sourceCode', ['getSourceCodeById', 'getSourceChannelList', 'updateSourceCodeById', 'saveSourceCode']),
    async initData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadSourceCode(),
          this.loadSourceChannel()
        ])
      } finally {
        this.loading = false
      }
    },
    async loadSourceCode () {
      if (this.sourceCodeId) {
        const params = {
          sourceCodeId: this.sourceCodeId,
          params: {
            company_id: this.companyId
          }
        }
        const data = await this.getSourceCodeById(params)
        this.sourceCodeData = data
      }
    },
    async loadSourceChannel () {
      const params = {
        status: 1,
        company_id: this.companyId,
        global_channel: 1
      }
      const resp = await this.getSourceChannelList(params)
      if (resp) {
        const vm = this
        const channelList = [
          {
            id: '-2',
            get channelName () {
              return vm.$t(`${vm.i18nPrefix}.label.parentChannel`)
            }
          },
          {
            id: '-1',
            get channelName () {
              return vm.$t(`${vm.i18nPrefix}.label.weChatChannel`)
            }
          }
        ]
        _.forEach(resp.result, channel => {
          addI18nProperty(channel, 'channelName', 'cn_name', 'name')
        })
        channelList.push(...resp.result)
        this.sourceChannelList = channelList
      }
    },
    async submit () {
      let resp
      if (this.isEditMode) {
        const data = {
          sourceCodeId: this.sourceCodeId,
          data: {
            company_id: this.companyId,
            channel_id: this.sourceCodeData.channel_id
          }
        }
        resp = await this.updateSourceCodeById(data)
      } else {
        const data = {
          ...this.sourceCodeData,
          company_id: this.companyId
        }
        resp = await this.saveSourceCode(data)
      }
      if (resp) {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        if (this.isEditMode) {
          this.$router.replace({ path: '/v-admin/source-channel/management/code', query: this.$route.query })
        } else {
          this.$router.replace({ path: '/v-admin/source-channel/management/code', query: { ...this.$route.query, status: 0, keyword: null } })
        }
      }
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        await this.submit()
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.source-code-editor-page {
  .source-code-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .source-code-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
